<template>
    <div>
        <limitless-page-header :loading="busy_loading" :title="'Bildbank'" :sub-title="image_name" :title-id="image_id">
            <template #header-elements>
                <limitless-page-header-button :route="{ name: 'mediabank.list' }" icon="icon-circle-left2">Tillbaka</limitless-page-header-button>
            </template>
        </limitless-page-header>

        <div class="content">
            <tb-skeleton v-if="busy_loading" :height="100" :theme="'opacity'" :shape="'radius'" bg-color="#dcdbdc"></tb-skeleton>

            <div v-else-if="!busy_loading && image">
                <div class="row">
                    <div class="col-lg-6">
                        <div class="card">
                            <div class="card-body">
                                <ValidationObserver ref="imageform">
                                    <form @submit.prevent="saveImage">
<!--                                        <input-with-validation-->
<!--                                            vid="ImageName"-->
<!--                                            label="Namn"-->
<!--                                            rules=""-->
<!--                                            v-model="image.ImageName"-->
<!--                                        ></input-with-validation>-->
                                        <input-with-validation
                                            vid="orgfilename"
                                            label="Originalnamn"
                                            rules=""
                                            v-model="image.orgfilename"
                                            disabled
                                        ></input-with-validation>
                                        <select-with-validation
                                            vid="CatID"
                                            component="mediabank-categories-filter"
                                            v-model="image.CatID"
                                            label="Kategori"
                                            rules="required"
                                            :reduce="(opt) => opt.id"
                                        ></select-with-validation>
                                        <hr>
                                        <div>
                                            Width: {{ image.Width }} px<br>
                                            Height: {{ image.Height }} px
                                        </div>
                                    </form>
                                </ValidationObserver>
                            </div>
                            <div class="card-footer">
                                <div class="d-flex justify-content-end">
                                    <limitless-button v-if="image.ImageID > 0" ref="btndeleteimage" type="button" @click="deleteImage" :busy="busy_saving"
                                                      :disabled="busy_replacing" icon="icon-cross2" btn-class="btn-danger" class="mr-auto">Radera</limitless-button>
                                    <limitless-button ref="btnsaveimage" type="button" @click="saveImage" :busy="busy_saving" :disabled="busy_replacing" icon="icon-floppy-disk">Spara</limitless-button>
                                </div>
                            </div>
                        </div>
                        <div v-if="image.ImageID > 0 && hasRole('super-admin')" class="card">
                            <div class="card-header"><h6 class="card-title"><i class="icon-upload mr-2" aria-hidden="true"></i>Ersätt fil</h6></div>
                            <div class="card-body">
                                <p>Bildens ID behålls. Den nya filen används överallt där bilden används. Alla tidigare genererade storlekar tas bort och skapas på nytt.</p>
                                <label for="replacement-file">Ny fil</label>
                                <single-file-uploader input-id="replacement-file" ref="replacementfile" :url="`/vue/mediabank/images/${id}/replace`"
                                    @input="replacement_file = $event; replacement_error = ''" :disabled="busy_replacing || busy_saving"
                                    accept=".jpg,.jpeg,.png,.gif,.pdf,.mp4" extensions="jpg,jpeg,png,gif,pdf,mp4" :max-size="50 * 1024 * 1024" />
                                <small class="form-text text-muted">JPEG, PNG, GIF, PDF eller MP4. Högst 50 MB.</small>
                                <p v-if="replacement_error" class="alert alert-danger mt-3 mb-0" role="alert">{{ replacement_error }}</p>
                                <p v-if="replacement_notice" class="alert alert-success mt-3 mb-0" role="status">{{ replacement_notice }}</p>
                            </div>
                            <div class="card-footer text-right">
                                <limitless-button icon="icon-upload" :busy="busy_replacing" :disabled="!replacement_file || busy_saving" @click="replaceFile">Ersätt och generera om</limitless-button>
                            </div>
                        </div>
                    </div>
                    <div class="col-lg-6" v-if="image.ImageID > 0">
                        <img :src="original_url" style="max-width: 400px; border: 1px solid #ccc;" :alt="image_name" />
                        <div>
                            <a :href="original_url" target="_blank">Visa original</a>
                        </div>
                    </div>
                </div>
            </div>
        </div> <!-- /content -->
    </div>
</template>

<script>
import apiClient from '@services/ApiClient';
import SingleFileUploader from '@components/SingleFileUploader';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import LimitlessPageHeaderButton from '@components/Layout/partials/LimitlessPageHeaderButton';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import getError from '@services/ErrorHelper';
import InputWithValidation from '@components/Form/InputWithValidation';
import SelectWithValidation from '@components/Form/SelectWithValidation';
import { hasRole } from '@services/Helpers';

export default {
    name: 'MediabankImageEdit',
    components: {
        SingleFileUploader,
        SelectWithValidation,
        InputWithValidation,
        LimitlessButton,
        LimitlessPageHeader,
        LimitlessPageHeaderButton,
    },
    props: {
        id: {
            type: [Number, String],
            required: true,
        },
    },
    data: () => ({
        busy_loading: true,
        busy_saving: false,
        busy_replacing: false,
        replacement_file: null,
        replacement_error: '',
        replacement_notice: '',
        preview_version: 0,
        busy_translating: false,
        image: null,
        languages: [],
    }),
    mounted() {
        this.getImage();
    },
    methods: {
        hasRole,
        async replaceFile() {
            if (this.busy_replacing || this.busy_saving || !this.replacement_file || !this.hasRole('super-admin')) return;
            this.replacement_error = '';
            this.replacement_notice = '';
            if (this.replacement_file.size > 50 * 1024 * 1024) { this.replacement_error = 'Filen får vara högst 50 MB.'; return; }
            this.busy_replacing = true;
            try {
                const result = await this.$swal({
                    title: 'Ersätt bildfilen?',
                    text: 'Originalfilen ersätts och alla tidigare genererade storlekar raderas och skapas på nytt. Detta påverkar alla ställen där bilden används.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Ja, ersätt filen',
                    cancelButtonText: 'Avbryt',
                });
                if (!result.isConfirmed) return;
                const response = await this.$refs.replacementfile.upload({ confirmed: '1' });
                this.image = { ...response.file, CatID: this.image.CatID };
                this.$refs.replacementfile.clear();
                this.preview_version = Date.now();
                this.replacement_file = null;
                this.replacement_notice = 'Filen har ersatts och alla bildstorlekar har genererats om.';
            } catch (error) {
                if (error.response?.status === 413) {
                    this.replacement_error = 'Servern avvisade uppladdningen eftersom den är för stor. Kontrollera serverns uppladdningsgräns. Filen får vara högst 50 MB.';
                    return;
                }
                const parsed = error.response?.data?.errors;
                this.replacement_error = typeof parsed === 'object' && parsed ? Object.values(parsed).flat().join(' ') : (error.response?.data?.message || error.message || 'Filen kunde inte ersättas.');
            } finally { this.busy_replacing = false; }
        },
        getImage() {
            this.busy_loading = true;
            apiClient.get('/vue/mediabank/images/' + this.id + '')
                .then((response) => {
                    this.image = response.data;
                })
                .finally(() => {
                    this.busy_loading = false;
                });
        },
        async saveImage() {
            if (this.busy_replacing || this.busy_saving) return;
            const isValid = await this.$refs.imageform.validate();
            if (isValid) {
                this.busy_saving = true;
                await apiClient.post('/vue/mediabank/images/' + this.id, this.image)
                    .then((response) => {
                        if (parseInt(this.id, 10) !== parseInt(response.data.ImageID, 10)) {
                            window.location.href = '/mediabank/' + response.data.ImageID;
                        }
                        this.image = response.data;
                        this.$refs.btnsaveimage.triggerSaved();
                    })
                    .catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.imageform.setErrors(parsederror);
                        }
                        this.$refs.btnsaveimage.triggerFailed();
                    })
                    .finally(() => {
                        this.busy_saving = false;
                    });
            }
        },
        async deleteImage() {
            if (this.busy_replacing || this.busy_saving) return;
            let doChange = false;
            await this.$swal({
                text: 'Är du säker att du vill radera denna bild?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Ja, ta bort!',
                cancelButtonText: 'Avbryt',
            }).then((result) => {
                if (result.isConfirmed) {
                    doChange = true;
                }
            });
            if (doChange) {
                this.busy_saving = true;
                apiClient.delete('/vue/mediabank/images/' + this.id)
                    .then(() => {
                        this.$router.push({ name: 'mediabank.list' });
                        this.busy_saving = false;
                    })
                    .catch(() => {
                        this.$refs.btndeleteimage.triggerFailed();
                    })
                    .finally(() => {
                        this.busy_saving = false;
                    });
            }
        },
    },
    computed: {
        original_url() {
            const url = this.image?.internal_urls?.original || '';
            return url && this.preview_version ? `${url}?v=${this.preview_version}` : url;
        },
        image_name() {
            // eslint-disable-next-line no-nested-ternary
            return (this.image && this.image.ImageID > 0 ? (this.image.ImageName ? this.image.ImageName : this.image.orgfilename) : 'Ny bild');
        },
        image_id() {
            return (this.image && this.image.ImageID > 0 ? this.image.ImageID : '');
        },
    },
};

</script>
